import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'
import { Badge } from '../../ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog'
import { 
  Award, 
  Plus, 
  Edit, 
  Trash2, 
  MoreHorizontal,
  Shield
} from 'lucide-react'
import EventEndpoints from '../../../endpoints/EventEndpoints'
import { useEventContext } from '../EventContext'
import { toast } from 'sonner'

interface AccreditationLevel {
  id: number
  dateCreated: Date
  name: string
  state: 'REFERENCED' | 'UNREFERENCED'
  badgeText: string
}

interface AccreditationLevelRequest {
  name: string
  badgeText: string
}

const defaultAccreditationLevel: AccreditationLevelRequest = {
  name: '',
  badgeText: '',
}

export const AccreditationLevels: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>()
  const { event, setEvent, canEdit } = useEventContext()
  const [levels, setLevels] = useState<AccreditationLevel[]>([])
  const [saving, setSaving] = useState(false)

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedLevel, setSelectedLevel] = useState<AccreditationLevel | null>(null)

  const [formData, setFormData] = useState<AccreditationLevelRequest>(defaultAccreditationLevel)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (event) {
      setLevels(event.accreditationLevels || [])
    }
  }, [event])

  const validateForm = (data: AccreditationLevelRequest): Record<string, string> => {
    const errors: Record<string, string> = {}

    if (!data.name.trim()) {
      errors.name = 'Name is required'
    }

    if (!data.badgeText.trim()) {
      errors.badgeText = 'Badge text is required'
    }

    return errors
  }

  const handleCreate = async () => {
    const errors = validateForm(formData)
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    try {
      setSaving(true)
      // Create new accreditation level
      const result = await EventEndpoints.addEventAccreditationLevel(parseInt(eventId!), formData)

      // Update the event state with the new accreditation level
      setEvent(result.parent)
      setLevels(result.parent.accreditationLevels || [])
      
      toast.success('Accreditation level created successfully')
      setCreateDialogOpen(false)
      setFormData(defaultAccreditationLevel)
      setFormErrors({})
    } catch (error: any) {
      console.error('Failed to create accreditation level:', error)
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to create accreditation level'
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = async () => {
    if (!selectedLevel) return

    const errors = validateForm(formData)
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    // Close dialog immediately to prevent focus issues
    setEditDialogOpen(false)
    setSaving(true)

    try {
      // Update accreditation level using patch requests
      await EventEndpoints.editEventAccreditationLevel(selectedLevel.id, { field: 'name', value: formData.name })
      await EventEndpoints.editEventAccreditationLevel(selectedLevel.id, { field: 'badgeText', value: formData.badgeText })

      // Reload event data to get updated accreditation levels
      const updatedEvent = await EventEndpoints.getEvent(parseInt(eventId!))
      setEvent(updatedEvent)
      setLevels(updatedEvent.accreditationLevels || [])

      toast.success('Accreditation level updated successfully')
    } catch (error: any) {
      console.error('Failed to update accreditation level:', error)
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to update accreditation level'
      toast.error(errorMsg)
    } finally {
      // Clean up all state
      setSaving(false)
      setSelectedLevel(null)
      setFormData(defaultAccreditationLevel)
      setFormErrors({})
    }
  }

  const handleDelete = async () => {
    if (!selectedLevel) return

    // Close dialog immediately to prevent focus issues
    setDeleteDialogOpen(false)
    setSaving(true)

    try {
      // Delete accreditation level
      const updatedEvent = await EventEndpoints.deleteEventAccreditationLevel(selectedLevel.id)
      setEvent(updatedEvent)
      setLevels(updatedEvent.accreditationLevels || [])

      toast.success('Accreditation level deleted successfully')
    } catch (error: any) {
      console.error('Failed to delete accreditation level:', error)
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to delete accreditation level'
      toast.error(errorMsg)
    } finally {
      // Clean up all state
      setSaving(false)
      setSelectedLevel(null)
    }
  }

  const openCreateDialog = () => {
    setSelectedLevel(null)
    setFormData(defaultAccreditationLevel)
    setFormErrors({})
    setCreateDialogOpen(true)
  }

  const openEditDialog = (level: AccreditationLevel) => {
    setSelectedLevel(level)
    setFormData({
      name: level.name,
      badgeText: level.badgeText,
    })
    setFormErrors({})
    setEditDialogOpen(true)
  }

  const openDeleteDialog = (level: AccreditationLevel) => {
    setSelectedLevel(level)
    setDeleteDialogOpen(true)
  }

  if (!event) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  const sortedLevels = [...levels].sort((a, b) => a.name.localeCompare(b.name))

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                Accreditation Levels
              </CardTitle>
              <CardDescription>
                Manage access levels and permissions for {event?.name || 'this event'}
              </CardDescription>
            </div>
            <Button onClick={openCreateDialog} disabled={!canEdit}>
              <Plus className="h-4 w-4 mr-2" />
              Add Level
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {levels.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No accreditation levels</h3>
              <p className="text-muted-foreground mb-4">
                Create your first accreditation level to start organizing access permissions.
              </p>
              <Button onClick={openCreateDialog} disabled={!canEdit}>
                <Plus className="h-4 w-4 mr-2" />
                Create Level
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Badge Text</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date Created</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedLevels.map((level) => (
                  <TableRow key={`level-${level.id}-${level.name}`}>
                    <TableCell className="font-medium">{level.name}</TableCell>
                    <TableCell className="max-w-xs truncate">
                      {level.badgeText}
                    </TableCell>
                    <TableCell>
                      <Badge variant={level.state === 'REFERENCED' ? 'default' : 'secondary'}>
                        {level.state === 'REFERENCED' ? 'In Use' : 'Unused'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(level.dateCreated).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {canEdit && (
                        <DropdownMenu key={`dropdown-${level.id}`}>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(level)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => openDeleteDialog(level)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create Accreditation Level</DialogTitle>
            <DialogDescription>
              Add a new accreditation level for this event.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name *
              </Label>
              <div className="col-span-3">
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., VIP, Staff, General"
                  className={formErrors.name ? 'border-red-500' : ''}
                />
                {formErrors.name && (
                  <p className="text-sm text-red-500 mt-1">{formErrors.name}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="badgeText" className="text-right">
                Badge Text *
              </Label>
              <div className="col-span-3">
                <Input
                  id="badgeText"
                  value={formData.badgeText}
                  onChange={(e) => setFormData(prev => ({ ...prev, badgeText: e.target.value }))}
                  placeholder="Text to display on the badge"
                  className={formErrors.badgeText ? 'border-red-500' : ''}
                />
                {formErrors.badgeText && (
                  <p className="text-sm text-red-500 mt-1">{formErrors.badgeText}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">This text will appear on printed badges</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateDialogOpen(false)
                setFormData(defaultAccreditationLevel)
                setFormErrors({})
                setSelectedLevel(null)
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating...' : 'Create Level'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            // Only allow closing if not currently saving
            if (!saving) {
              setEditDialogOpen(false)
              setFormData(defaultAccreditationLevel)
              setFormErrors({})
              setSelectedLevel(null)
            }
          } else {
            setEditDialogOpen(true)
          }
        }}
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Accreditation Level</DialogTitle>
            <DialogDescription>
              Update the accreditation level details.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-name" className="text-right">
                Name *
              </Label>
              <div className="col-span-3">
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., VIP, Staff, General"
                  className={formErrors.name ? 'border-red-500' : ''}
                />
                {formErrors.name && (
                  <p className="text-sm text-red-500 mt-1">{formErrors.name}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-badgeText" className="text-right">
                Badge Text *
              </Label>
              <div className="col-span-3">
                <Input
                  id="edit-badgeText"
                  value={formData.badgeText}
                  onChange={(e) => setFormData(prev => ({ ...prev, badgeText: e.target.value }))}
                  placeholder="Text to display on the badge"
                  className={formErrors.badgeText ? 'border-red-500' : ''}
                />
                {formErrors.badgeText && (
                  <p className="text-sm text-red-500 mt-1">{formErrors.badgeText}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">This text will appear on printed badges</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditDialogOpen(false)
                setFormData(defaultAccreditationLevel)
                setFormErrors({})
                setSelectedLevel(null)
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            // Only allow closing if not currently saving
            if (!saving) {
              setDeleteDialogOpen(false)
              setSelectedLevel(null)
            }
          } else {
            setDeleteDialogOpen(true)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Accreditation Level</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedLevel?.name}"? This action cannot be undone.
              Any identities assigned to this level will need to be reassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setDeleteDialogOpen(false)
                setSelectedLevel(null)
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}